import { HappyReturnsBillingMode } from "@redotech/congo-enums/happy-returns-billing-mode";
import { HappyReturnsDropoffMethod } from "@redotech/congo-enums/happy-returns-dropoff-method";
import { HappyReturnsAuditEvent } from "@redotech/happyreturns-client/webhook-payload";
import { Currency } from "@redotech/money/currencies";
import {
  BLUE_YONDER_PROVIDER,
  type DropoffProviderType,
  HAPPY_RETURNS_PROVIDER,
  RETURN_BEAR_PROVIDER,
  VIRTUAL_PROVIDER,
} from "@redotech/redo-return-dropoff-provider-common/dropoff-provider";
import {
  VirtualMiddleMileCarrier,
  VirtualServiceLevel,
} from "@redotech/redo-return-dropoff-provider-common/virtual-middle-mile";
import { DateFromDateOrIsoStringSchema } from "@redotech/zod-util/date-schema";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { WebhookEventType } from "../integrations/returns/blueyonder/blueyonder";
import { UniUniWebhookDataSchema } from "../integrations/returns/uni-uni/types";

// =============================================================================
// Address
// =============================================================================

const DropoffLocationAddressSchema_ = z.object({
  street1: z.string(),
  street2: z.string().optional(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  country: z.string(),
});
export const DropoffLocationAddressSchema: z.ZodType<DropoffLocationAddress> =
  DropoffLocationAddressSchema_;
export interface DropoffLocationAddress extends z.infer<
  typeof DropoffLocationAddressSchema_
> {}

// =============================================================================
// Dropoff Location Result (per-provider discriminated union)
// =============================================================================

const HoursEntrySchema_ = z.object({ open: z.string(), close: z.string() });

/** Absent on entries stored before carriers were recorded; read with `virtualMiddleMileCarrierOf`. */
const virtualMiddleMileFields = {
  carrier: createEnum(VirtualMiddleMileCarrier).optional(),
  serviceLevel: createEnum(VirtualServiceLevel).optional(),
};

/** Just the carrier tags, so a reader need not parse the whole metadata. */
export const VirtualMiddleMileTagsSchema = z.object(virtualMiddleMileFields);

const VirtualLocationMetadataSchema_ = z.object({
  ...virtualMiddleMileFields,
  phoneNumber: z.string().optional(),
  hours: z
    .object({
      sun: z.array(HoursEntrySchema_).optional(),
      mon: z.array(HoursEntrySchema_).optional(),
      tue: z.array(HoursEntrySchema_).optional(),
      wed: z.array(HoursEntrySchema_).optional(),
      thu: z.array(HoursEntrySchema_).optional(),
      fri: z.array(HoursEntrySchema_).optional(),
      sat: z.array(HoursEntrySchema_).optional(),
    })
    .optional(),
});
export const VirtualLocationMetadataSchema: z.ZodType<VirtualLocationMetadata> =
  VirtualLocationMetadataSchema_;
export interface VirtualLocationMetadata extends z.infer<
  typeof VirtualLocationMetadataSchema_
> {}

const dropoffLocationResultBase = {
  id: z.string(),
  name: z.string(),
  address: DropoffLocationAddressSchema,
  distance: z
    .string()
    .describe("Display string, e.g. '1.2 miles'. MUST BE IN MILES"),
  /** Numeric distance in miles for computation. Optional: drop-offs persisted before this field won't have it. */
  distanceMiles: z.number().optional(),
  outsideConfiguredRadius: z.boolean().optional(),
  configuredRadiusMiles: z.number().optional(),
  geo: z.object({ latitude: z.number(), longitude: z.number() }),
};

const BlueYonderHoursEntrySchema_ = z.object({
  isOpen: z.boolean(),
  hours: z.array(z.tuple([z.string(), z.string()])).nullish(),
});

const BlueYonderOpeningHoursSchema_ = z.object({
  monday: BlueYonderHoursEntrySchema_,
  tuesday: BlueYonderHoursEntrySchema_,
  wednesday: BlueYonderHoursEntrySchema_,
  thursday: BlueYonderHoursEntrySchema_,
  friday: BlueYonderHoursEntrySchema_,
  saturday: BlueYonderHoursEntrySchema_,
  sunday: BlueYonderHoursEntrySchema_,
});

const BlueYonderLocationMetadataSchema_ = z.object({
  // The merchant's configured BlueYonder return destination location ID.
  // Sourced from the merchant's address (blueYonderLocationId) and passed to
  // the BlueYonder createReturn API as destinationLocation.locationId.
  blueYonderLocationId: z.string().optional(),
  locationType: z.string().optional(),
  phoneNumber: z.string().optional(),
  openingHours: BlueYonderOpeningHoursSchema_.optional(),
});
export const BlueYonderLocationMetadataSchema: z.ZodType<BlueYonderLocationMetadata> =
  BlueYonderLocationMetadataSchema_;
export interface BlueYonderLocationMetadata extends z.infer<
  typeof BlueYonderLocationMetadataSchema_
> {}

const HappyReturnsOpeningHoursSchema = z.object({
  closingTime: z.string().optional(),
  dayOfWeek: z.number().optional(),
  openingTime: z.string().optional(),
});

const HappyReturnsLocationMetadataSchema_ = z.object({
  billingMode: createEnum(HappyReturnsBillingMode).optional(),
  dropoffMethodId: createEnum(HappyReturnsDropoffMethod),
  dropoffMethodsOffered: z
    .array(createEnum(HappyReturnsDropoffMethod))
    .optional(),
  locationId: z.string(),
  merchantFee: z.object({
    amount: z.number().nonnegative(),
    currency: createEnum(Currency),
  }),
  addressId: z.number().optional(),
  hoursMayVary: z.boolean().optional(),
  openingHours: z.array(HappyReturnsOpeningHoursSchema).optional(),
  timeZone: z.string().optional(),
});
export const HappyReturnsLocationMetadataSchema: z.ZodType<HappyReturnsLocationMetadata> =
  HappyReturnsLocationMetadataSchema_;
export interface HappyReturnsLocationMetadata extends z.infer<
  typeof HappyReturnsLocationMetadataSchema_
> {}

const DropoffLocationResultSchema_ = z.discriminatedUnion("provider", [
  z.object({
    ...dropoffLocationResultBase,
    provider: z.literal(VIRTUAL_PROVIDER),
    providerMetadata: VirtualLocationMetadataSchema.optional(),
  }),
  z.object({
    ...dropoffLocationResultBase,
    provider: z.literal(BLUE_YONDER_PROVIDER),
    providerMetadata: BlueYonderLocationMetadataSchema.optional(),
  }),
  z.object({
    ...dropoffLocationResultBase,
    provider: z.literal(HAPPY_RETURNS_PROVIDER),
    providerMetadata: HappyReturnsLocationMetadataSchema,
  }),
  z.object({
    ...dropoffLocationResultBase,
    provider: z.literal(RETURN_BEAR_PROVIDER),
    providerMetadata: z.object({}).optional(),
  }),
]);
export const DropoffLocationResultSchema: z.ZodType<DropoffLocationResult> =
  DropoffLocationResultSchema_;
export type DropoffLocationResult = {} & z.infer<
  typeof DropoffLocationResultSchema_
>;

export const DROPOFF_PROVIDER_LOCATION_METADATA_SCHEMAS = {
  VIRTUAL: VirtualLocationMetadataSchema,
  RETURN_BEAR: z.object({}),
  BLUE_YONDER: BlueYonderLocationMetadataSchema,
  HAPPY_RETURNS: HappyReturnsLocationMetadataSchema,
} satisfies Record<DropoffProviderType, z.ZodType>;

// =============================================================================
// Get Dropoff Locations Result
// =============================================================================

const GetDropoffLocationsResultSchema_ = z.object({
  locations: z.array(DropoffLocationResultSchema),
});
export const GetDropoffLocationsResultSchema: z.ZodType<GetDropoffLocationsResult> =
  GetDropoffLocationsResultSchema_;
export interface GetDropoffLocationsResult extends z.infer<
  typeof GetDropoffLocationsResultSchema_
> {}

// =============================================================================
// Dropoff QR Code Result (per-provider discriminated union)
// =============================================================================

const VirtualQrCodeMetadataSchema_ = z.object({
  ...virtualMiddleMileFields,
  dashCode: z.string().optional(),
  expiresAt: z.number().optional(),
  returnVerification: z.literal(true).optional(),
  uniUniOrderNumber: z.string().optional(),
  /** Created but maybe unpaid; a purchase retry resumes it instead of creating another. */
  uniUniDraftOrderNumber: z.string().optional(),
  uniUniTrackingId: z.string().optional(),
  uniUniLabelUrl: z.string().optional(),
  uniUniDropoffPostalCode: z.string().optional(),
  uniUniPurchasedAt: z.number().optional(),
  uniUniRegion: z.string().optional(),
  uniUniVoidedAt: z.number().optional(),
  uniUniVoidError: z.string().optional(),
  uniUniVoidErrorCode: z.string().optional(),
  uniUniVoidAttempts: z.number().optional(),
  webhookEvents: z.array(UniUniWebhookDataSchema).default([]).nullish(),
});
export const VirtualQrCodeMetadataSchema: z.ZodType<VirtualQrCodeMetadata> =
  VirtualQrCodeMetadataSchema_;
export interface VirtualQrCodeMetadata extends z.infer<
  typeof VirtualQrCodeMetadataSchema_
> {}

const dropoffQrCodeResultBase = {
  shipmentGroupId: z.string(),
  externalShipmentId: z.string().optional(),
  qrCode: z.string(),
  qrCodeUrl: z.string(),
  trackingNumber: z.string().nullish(),
  dropoffFlatRateFee: z.number().nullish(),
};

export const BlueYonderWebhookEventSchema = z.object({
  eventType: createEnum(WebhookEventType),
  status: z.string(),
  eventDateTime: DateFromDateOrIsoStringSchema,
  eventId: z.string(),
});

export type BlueYonderWebhookEvent = z.infer<
  typeof BlueYonderWebhookEventSchema
>;

const BlueYonderQrCodeMetadataSchema_ = z.object({
  blueYonderLocationId: z.string().nullish(),
  returnId: z.string().nullish(),
  labelValue: z.string().nullish(),
  caseId: z.string().nullish(),
  returnAuthorizationNumber: z.string().nullish(),
  webhookEvents: z.array(BlueYonderWebhookEventSchema).optional(),
});
export const BlueYonderQrCodeMetadataSchema: z.ZodType<BlueYonderQrCodeMetadata> =
  BlueYonderQrCodeMetadataSchema_;
export interface BlueYonderQrCodeMetadata extends z.infer<
  typeof BlueYonderQrCodeMetadataSchema_
> {}

const HappyReturnsQrCodeMetadataSchema_ = z.object({
  billingMode: createEnum(HappyReturnsBillingMode).optional(),
  dropoffMethodId: createEnum(HappyReturnsDropoffMethod),
  dropoffMethodsOffered: z
    .array(createEnum(HappyReturnsDropoffMethod))
    .optional(),
  locationId: z.string(),
  merchantFee: z.object({
    amount: z.number().nonnegative(),
    currency: createEnum(Currency),
  }),
  rmaId: z.string().optional(),
  appleWalletPassUrl: z.string().optional(),
  googleWalletPassUrl: z.string().optional(),
  audit: z
    .object({
      event: createEnum(HappyReturnsAuditEvent),
      riskJustifications: z.array(z.string()),
      items: z.array(
        z.object({
          auditCompletedAt: z.string().nullish(),
          auditStatus: createEnum(HappyReturnsAuditEvent),
          happyReturnsItemId: z.string(),
          inspectionImageUrls: z.array(z.string()),
          orderLineItemId: z.string(),
          orderNumber: z.string().optional(),
          productName: z.string().optional(),
          sku: z.string().optional(),
          upc: z.string().optional(),
        }),
      ),
    })
    .optional(),
  items: z
    .array(
      z.object({
        happyReturnsItemId: z.string(),
        orderLineItemId: z.string(),
        orderNumber: z.string(),
        sku: z.string().optional(),
        upc: z.string().optional(),
      }),
    )
    .optional(),
});
export const HappyReturnsQrCodeMetadataSchema: z.ZodType<HappyReturnsQrCodeMetadata> =
  HappyReturnsQrCodeMetadataSchema_;
export interface HappyReturnsQrCodeMetadata extends z.infer<
  typeof HappyReturnsQrCodeMetadataSchema_
> {}

const DropoffQrCodeResultSchema_ = z.discriminatedUnion("provider", [
  z.object({
    ...dropoffQrCodeResultBase,
    provider: z.literal(VIRTUAL_PROVIDER),
    providerMetadata: VirtualQrCodeMetadataSchema,
  }),
  z.object({
    ...dropoffQrCodeResultBase,
    provider: z.literal(BLUE_YONDER_PROVIDER),
    providerMetadata: BlueYonderQrCodeMetadataSchema,
  }),
  z.object({
    ...dropoffQrCodeResultBase,
    provider: z.literal(HAPPY_RETURNS_PROVIDER),
    providerMetadata: HappyReturnsQrCodeMetadataSchema,
  }),
  z.object({
    ...dropoffQrCodeResultBase,
    provider: z.literal(RETURN_BEAR_PROVIDER),
    providerMetadata: z.object({}),
  }),
]);
export const DropoffQrCodeResultSchema: z.ZodType<DropoffQrCodeResult> =
  DropoffQrCodeResultSchema_;
export type DropoffQrCodeResult = {} & z.infer<
  typeof DropoffQrCodeResultSchema_
>;

export const DROPOFF_PROVIDER_METADATA_SCHEMAS = {
  VIRTUAL: VirtualQrCodeMetadataSchema,
  RETURN_BEAR: z.object({}),
  BLUE_YONDER: BlueYonderQrCodeMetadataSchema,
  HAPPY_RETURNS: HappyReturnsQrCodeMetadataSchema,
} satisfies Record<DropoffProviderType, z.ZodType>;
